import { Dispatch } from 'redux';
import {
  buyBonusCreditsApi,
  getBillingAddressApi,
  getAppBillingDetailsApi,
  getAppBillingShortDetailsApi,
  updateBillingAddressApi,
  upgradeContactsNumberApi,
  upgradeMailboxAccountsNumberApi,
  upgradePlanCreditsApi,
} from '@api/billing-app.api';

import loadingStatuses from '@constants/loadingStatuses';
import {
  setAppBillingData,
  setAppBillingLs,
  setAppBillingCreditsLs,
  setAppBillingCreditsData,
  setAppBillingAddressLs,
  setAppBillingAddressData,
} from '@redux/actions/billing-app.actions';
import {
  BillingAddressType,
  BillingCycleTypeValue,
  BillingDetailsShortType,
  BillingSubscriptionTypeMapValue,
  BillingType,
} from '@ts/billing-app.types';
import { addNotification } from '@redux/actions/notifications.actions';
import { setActivePaymentStatusInAppState } from '@redux/actions/appState.actions';
import {
  BillingSubscriptionCycle,
  BillingSubscriptionCycleMap,
} from 'respona_api/generated/common_pb';
import gtm from 'src/libs/gtm';
import { isSemrushMode } from '@constants/app-modes';
import { GTM_TYPES } from '@constants/gtmTypes';

export const fetchAppBillingDetails =
  (silent = false) =>
  (dispatch: Dispatch): Promise<BillingType> => {
    !silent && dispatch(setAppBillingLs(loadingStatuses.INITED));

    return getAppBillingDetailsApi()
      .then((billing) => {
        dispatch(setAppBillingData(billing));
        return billing;
      })
      .finally(() => {
        dispatch(setAppBillingLs(loadingStatuses.LOADED));
      });
  };

export const fetchAppBillingCredits =
  (silent = false) =>
  (dispatch: Dispatch): Promise<BillingDetailsShortType> => {
    !silent && dispatch(setAppBillingCreditsLs(loadingStatuses.INITED));

    return getAppBillingShortDetailsApi()
      .then((billingCredits) => {
        if (!isSemrushMode()) {
          dispatch(setActivePaymentStatusInAppState(billingCredits.active));
        }
        dispatch(setAppBillingCreditsData(billingCredits));
        return billingCredits;
      })
      .finally(() => {
        dispatch(setAppBillingCreditsLs(loadingStatuses.LOADED));
      });
  };

export const fetchAppBillingAddress =
  () =>
  (dispatch: Dispatch): void => {
    dispatch(setAppBillingAddressLs(loadingStatuses.INITED));

    getBillingAddressApi()
      .then((address) => {
        dispatch(setAppBillingAddressData(address));
      })
      .finally(() => {
        dispatch(setAppBillingAddressLs(loadingStatuses.LOADED));
      });
  };

export const updateAppBillingAddress =
  (addressFields: BillingAddressType) =>
  (dispatch: Dispatch): void => {
    updateBillingAddressApi(addressFields).then((updateAddress) => {
      dispatch(setAppBillingAddressData(updateAddress));
      dispatch(addNotification({ title: 'Billing address updated', type: 'success' }));
    });
  };

export const upgradeAppPlanCreditsRequest =
  (
    credits: number,
    type: BillingSubscriptionTypeMapValue,
    cycle: BillingSubscriptionCycleMap[keyof BillingSubscriptionCycleMap],
    userEmail
  ) =>
  (dispatch) => {
    return upgradePlanCreditsApi(credits, type, cycle).then((response) => {
      dispatch(addNotification({ title: 'Subscription updated', type: 'success' }));
      dispatch(fetchAppBillingCredits(true));
      dispatch(fetchAppBillingDetails(true));
      gtm.makePayment(
        userEmail,
        response.optionalTransactionCost,
        response.optionalTransactionId,
        GTM_TYPES.CREDITS,
        response.optionalSubscriptionPrice,
        cycle,
        1
      );
    });
  };

export const buyAppBonusCreditsRequest =
  (bonusCreditsNumber: number, userEmail: string) => (dispatch) => {
    return new Promise<void>((res, rej) => {
      buyBonusCreditsApi(bonusCreditsNumber)
        .then((response) => {
          dispatch(
            addNotification({
              title: `You bought ${bonusCreditsNumber} bonus credits`,
              type: 'success',
            })
          );
          dispatch(fetchAppBillingCredits(true));
          dispatch(fetchAppBillingDetails(true));
          gtm.makePayment(
            userEmail,
            response.optionalTransactionCost,
            response.optionalTransactionId,
            GTM_TYPES.CREDITS,
            response.optionalSubscriptionPrice,
            BillingSubscriptionCycle.NONE,
            1
          );
          res();
        })
        .catch(rej);
    });
  };

export const upgradeAppMailboxAccountsNumberRequest =
  (mailboxesNumber: number, cycle: BillingCycleTypeValue, userEmail: string) => (dispatch) => {
    return upgradeMailboxAccountsNumberApi(mailboxesNumber).then((response) => {
      dispatch(addNotification({ title: 'Success mailbox upgrade', type: 'success' }));
      dispatch(fetchAppBillingDetails(true));
      gtm.makePayment(
        userEmail,
        response.optionalTransactionCost,
        response.optionalTransactionId,
        GTM_TYPES.EMAILS,
        response.optionalSubscriptionPrice,
        cycle,
        1
      );
    });
  };

export const upgradeAppContactsNumberRequest =
  (
    amount: number,
    type: BillingSubscriptionTypeMapValue,
    cycle: BillingCycleTypeValue,
    userEmail: string
  ) =>
  (dispatch) => {
    return upgradeContactsNumberApi(amount, type, cycle).then((response) => {
      dispatch(addNotification({ title: 'Contacts limit upgraded', type: 'success' }));
      dispatch(fetchAppBillingDetails(true));
      gtm.makePayment(
        userEmail,
        response.optionalTransactionCost,
        response.optionalTransactionId,
        GTM_TYPES.CONTACTS,
        response.optionalSubscriptionPrice,
        cycle,
        1
      );
    });
  };
